import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Loader2, Maximize2, X } from 'lucide-react'
import { api } from '@/lib/api'
import { useTaskStore } from '@/store/useTaskStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { GrowthListTable } from './GrowthListTable'
import { ActivityControls } from './ActivityControls'
import { SalesNavTable } from './SalesNavTable'
import { EngagersTable } from './EngagersTable'
import { PostsTable } from './PostsTable'
import { ProfilesTable } from './ProfilesTable'
import { ReadableArtifact } from './ReadableArtifact'

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return /not found|404/i.test(error.message)
}

const ARTIFACTS = [
  { id: 'growth_list', label: 'Growth List' },
  { id: 'filters', label: 'Filters' },
  { id: 'sales_nav', label: 'Sales Nav' },
  { id: 'engagers', label: 'Engagers' },
  { id: 'posts', label: 'Posts' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'interpretation', label: 'Intent' },
  { id: 'pipeline_log', label: 'Log' },
]

export function ArtifactViewer() {
  const { activeRunId, logs } = useTaskStore()
  const [selectedArtifact, setSelectedArtifact] = useState('growth_list')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [activityFilter, setActivityFilter] = useState('all')
  const [activityCandidates, setActivityCandidates] = useState<any[] | null>(null)
  useEffect(() => { setActivityCandidates(null); setActivityFilter('all') }, [activeRunId])
  const queryClient = useQueryClient()
  const prevStatusRef = useRef<string | null>(null)

  // Detect pipeline completion and trigger refresh
  useEffect(() => {
    if (!activeRunId) {
      prevStatusRef.current = null
      return
    }

    // Check if pipeline just completed
    const phaseEvents = logs.filter((log) => log.event === 'phase')
    if (phaseEvents.length > 0) {
      const latestPhase = phaseEvents[phaseEvents.length - 1]
      const isComplete = latestPhase.phase === 4 && latestPhase.status === 'done'
      const wasRunning = prevStatusRef.current === 'running'

      if (isComplete && wasRunning) {
        // Pipeline just completed! Invalidate artifact queries to refresh
        queryClient.invalidateQueries({ queryKey: ['artifact', activeRunId] })
        queryClient.invalidateQueries({ queryKey: ['run-summary', activeRunId] })
      }

      prevStatusRef.current = isComplete ? 'complete' : 'running'
    }
  }, [logs, activeRunId, queryClient])

  const { data, isLoading, error } = useQuery({
    queryKey: ['artifact', activeRunId, selectedArtifact],
    queryFn: () => api.getArtifact(activeRunId!, selectedArtifact),
    enabled: !!activeRunId,
    // Keep polling while artifact file does not exist yet; stop once available.
    refetchInterval: (query) => (isNotFoundError(query.state.error) ? 2000 : false),
    refetchIntervalInBackground: true,
  })

  const getArtifactCount = () => {
    if (!data) return null
    if (selectedArtifact === 'growth_list' && data.candidates) return `${data.candidates.length} candidates`
    if (selectedArtifact === 'sales_nav' && data.profiles) return `${data.profiles.length} profiles`
    if (selectedArtifact === 'engagers' && data.engagers) return `${data.engagers.length} engagers`
    if (selectedArtifact === 'posts' && data.results) return `${data.results.length} posts`
    if (selectedArtifact === 'profiles' && data.results) return `${data.results.length} profiles`
    if (Array.isArray(data)) return `${data.length} rows`
    if (typeof data === 'object') return `${Object.keys(data).length} fields`
    return null
  }

  if (!activeRunId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Artifacts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <p className="text-sm text-slate-500">Run a task to view artifacts</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const renderArtifact = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
        </div>
      )
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-400">Failed to load artifact</p>
          <p className="text-sm text-slate-500 mt-2">{(error as Error).message}</p>
        </div>
      )
    }

    if (!data) {
      return (
        <div className="text-center py-12">
          <p className="text-slate-500">No data available</p>
        </div>
      )
    }

    if (selectedArtifact === 'growth_list' && data.candidates) {
      return <><ActivityControls runId={activeRunId!} candidates={data.candidates} filter={activityFilter} setFilter={setActivityFilter} onCandidates={setActivityCandidates} /><GrowthListTable candidates={(activityCandidates || data.candidates).filter((c: any) => activityFilter === "all" || (activityFilter === "unknown" ? !["active", "inactive"].includes(c.activity_status) : c.activity_status === activityFilter))} /></>
    }

    if (selectedArtifact === 'sales_nav' && data.profiles) {
      return <SalesNavTable profiles={data.profiles} />
    }

    if (selectedArtifact === 'engagers' && data.engagers) {
      return <EngagersTable engagers={data.engagers} />
    }

    if (selectedArtifact === 'posts' && data.results) {
      return <PostsTable posts={data.results} />
    }

    if (selectedArtifact === 'profiles' && data.results) {
      return <ProfilesTable profiles={data.results} />
    }

    return <ReadableArtifact artifactId={selectedArtifact} data={data} />
  }

  const artifactBody = (
    <Tabs value={selectedArtifact} onValueChange={setSelectedArtifact}>
      <TabsList className="flex h-auto max-w-full flex-wrap justify-start gap-1 p-1">
        {ARTIFACTS.map((artifact) => (
          <TabsTrigger
            key={artifact.id}
            value={artifact.id}
            className="min-w-[88px] text-xs px-2 py-1.5"
          >
            {artifact.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <div className="mt-3 min-w-0">{renderArtifact()}</div>
    </Tabs>
  )

  const handleExport = async () => {
    if (!activeRunId || isExporting) return
    try {
      setIsExporting(true)
      await api.downloadExportXlsx(activeRunId, activityFilter)
    } catch (error) {
      console.error('Export XLSX failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-baseline gap-3">
            <CardTitle>Artifacts</CardTitle>
            {getArtifactCount() && (
              <span className="text-xs text-slate-500">{getArtifactCount()}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedArtifact === 'growth_list' && (
              <Button size="sm" variant="outline" onClick={() => setIsFullscreen(true)}>
                <Maximize2 className="w-4 h-4 mr-2" />
                Fullscreen
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {isExporting ? 'Exporting' : 'Export XLSX'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="min-w-0">
        {artifactBody}
      </CardContent>
      {isFullscreen && selectedArtifact === 'growth_list' && (
        <div className="fixed inset-0 z-50 bg-slate-950 p-4">
          <div className="flex h-full min-w-0 flex-col gap-3">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h2 className="text-lg font-semibold">Growth List</h2>
                {getArtifactCount() && <p className="text-xs text-slate-500">{getArtifactCount()}</p>}
              </div>
              <Button size="sm" variant="outline" onClick={() => setIsFullscreen(false)}>
                <X className="mr-2 h-4 w-4" />
                Close
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">{renderArtifact()}</div>
          </div>
        </div>
      )}
    </Card>
  )
}
